import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import queryString from 'query-string'
import moment from 'moment'
import { isEmpty, isNil } from 'lodash'
import pluralize from 'pluralize'
import { IconSettings, IconSparkles } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { InboxZero } from '@/components/inbox/components/InboxZero'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { Progress } from '@/components/progress/Progress'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { OptimizationChip } from '@/views/PromptEngineering/components/PromptFeedbackCard'
import {
  fetchCalibrationOrgSettings,
  fetchFeedbackOptimizationAgenda,
  saveCalibrationOrgSettings,
} from '@/reducers/promptFeedback/promptFeedback.actions'
import {
  CALIBRATION_POLL_MS,
  CALIBRATION_RESULT,
  CALIBRATION_STOP,
} from '@/reducers/promptFeedback/promptFeedback.redux'
import { POLLABLE_STATUSES } from '@/reducers/promptimizer/promptimizer.redux'

import './CalibrationAgentPanel.scss'

// The agent tab is calibration-specific, unlike TODO/ARCHIVE which every inbox shares.
export const AGENT_TAB_ID = 'agent'
export const TASK_NAME_PREFIX = 'Prompt Feedback: '
const promptName = (item) =>
  item.task_name?.startsWith(TASK_NAME_PREFIX)
    ? item.task_name.slice(TASK_NAME_PREFIX.length)
    : item.task_name || `Task ${item.task_id}`

const isActive = (item) => POLLABLE_STATUSES.includes(item.status)

const outcomeLine = (item) => {
  const { outcome } = item
  if (isNil(outcome?.feedback_total)) {
    return null
  }
  const regression = isNil(outcome.regression_total)
    ? ''
    : ` · ${outcome.regression_unchanged}/${outcome.regression_total} other calls unchanged`
  if (outcome.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED) {
    return `Already answers all ${outcome.feedback_total} reported calls${regression}`
  }
  if (outcome.result === CALIBRATION_RESULT.NO_CANDIDATE) {
    return `No usable rewrite — none fixed at least half of the ${outcome.feedback_total} reported calls`
  }
  return `Fixed ${outcome.feedback_fixed ?? 0}/${outcome.feedback_total} reported calls${regression}`
}

const AgendaRow = ({ item, orgName, onOpen }) => (
  <Card
    clickable
    compact
    className="agent-panel__row"
    data-testid={`agent-row-${item.task_id}`}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onOpen()
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="agent-panel__row-main">
      <div className="agent-panel__row-title">
        <strong>{promptName(item)}</strong>
        {orgName && (
          <Pill xs className="uppercase">
            {orgName}
          </Pill>
        )}
      </div>
      <div className="agent-panel__row-detail">
        {isActive(item) && <span>The agent is rewriting and testing this prompt now.</span>}
        {!isActive(item) && item.optimization_id && (
          <span>
            {outcomeLine(item) || 'No result recorded.'}
            {item.updated_at && ` · ${moment.utc(item.updated_at).local().fromNow()}`}
          </span>
        )}
        {!item.optimization_id && item.upcoming && (
          <span>
            {pluralize('customer report', item.n_corrections, true)} ready — picked up in the
            agent&apos;s next run (every hour).
          </span>
        )}
        {item.optimization_id && item.upcoming && !isActive(item) && (
          <span className="agent-panel__requeued"> · queued for the agent&apos;s next run</span>
        )}
      </div>
      {isActive(item) && <Progress magical indeterminate />}
    </div>
    {item.optimization_id ? (
      <OptimizationChip optimization={item} data-testid={`agent-chip-${item.task_id}`} />
    ) : (
      <Pill xs magical icon={<IconSparkles />} className="uppercase">
        Up next
      </Pill>
    )}
  </Card>
)

const AgentSettings = ({ orgLookup }) => {
  const dispatch = useDispatch()
  const [orgId, setOrgId] = useState(null)
  const [settings, setSettings] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [autoApply, setAutoApply] = useState(false)
  const [minFeedback, setMinFeedback] = useState('')
  const [saving, setSaving] = useState(false)

  const show = (data) => {
    setSettings(data)
    setAutoApply(Boolean(data?.auto_apply))
    setMinFeedback(isNil(data?.min_feedback) ? '' : String(data.min_feedback))
  }

  useEffect(() => {
    if (isNil(orgId)) {
      return undefined
    }
    let stale = false
    setSettings(null)
    setLoadFailed(false)
    dispatch(fetchCalibrationOrgSettings(orgId)).then((data) => {
      if (stale) {
        return
      }
      if (data) {
        show(data)
      } else {
        setLoadFailed(true)
      }
    })
    return () => {
      stale = true
    }
  }, [dispatch, orgId])

  const save = async () => {
    setSaving(true)
    const data = await dispatch(
      saveCalibrationOrgSettings(orgId, {
        auto_apply: autoApply,
        min_feedback: minFeedback === '' ? null : Number(minFeedback),
      })
    )
    if (data) {
      show(data)
    }
    setSaving(false)
  }

  const options = [...orgLookup.values()].map((org) => ({ value: org.id, label: org.name }))

  return (
    <BasicModal
      title="Agent settings"
      size="large"
      scrolling
      trigger={(open) => (
        <Button
          square
          basic
          onClick={open}
          aria-label="Agent settings"
          data-testid="agent-settings-button"
        >
          <IconSettings />
        </Button>
      )}
      footer={
        settings && (
          <Button brand loading={saving} onClick={save} data-testid="agent-org-save">
            Save
          </Button>
        )
      }
    >
      <section className="agent-panel__group field-stack" data-testid="agent-org-settings">
        <h4>Organization</h4>
        <Select
          options={options}
          value={orgId}
          onChange={(option) => setOrgId(option?.value ?? null)}
          placeholder="Select an organization"
          data-testid="agent-org-select"
        />
        {!isNil(orgId) && !settings && !loadFailed && <Loader size="small" centered />}
        {loadFailed && <ErrorMessage content="Could not load this organization's settings." />}
        {settings && (
          <>
            <Checkbox
              toggle
              label="Apply clean wins automatically"
              checked={autoApply}
              onChange={(_event, { checked }) => setAutoApply(checked)}
              data-testid="agent-org-auto-apply"
            />
            <p className="muted-text">
              When a run fixes every reported call, passes the regression check and leaves at least
              90% of recent calls unchanged, its prompt is saved to the scorecard without a review.
            </p>
            <FormField label="Reported calls needed before the agent runs">
              <TextInput
                type="number"
                min="1"
                value={minFeedback}
                onChange={(event) => setMinFeedback(event.target.value)}
                placeholder={`${settings.default_min_feedback} (default)`}
                data-testid="agent-org-min-feedback"
              />
            </FormField>
            <p className="muted-text">
              The hourly run only picks up a task with at least this many reported calls in the last
              30 days. &ldquo;Calibrate now&rdquo; ignores it.
            </p>
          </>
        )}
      </section>
    </BasicModal>
  )
}

const Group = ({ title, items, orgLookup, openTask }) => {
  if (isEmpty(items)) {
    return null
  }
  return (
    <section className="agent-panel__group">
      <h4>{title}</h4>
      {items.map((item) => (
        <AgendaRow
          key={item.task_id}
          item={item}
          orgName={orgLookup?.get(item.organization_id)?.name}
          onOpen={() => openTask(item)}
        />
      ))}
    </section>
  )
}

/**
 * The Calibration Agent's own surface: what the hourly optimization loop is doing right now, what it
 * will pick up next (the loop's real eligibility scan, not a client-side guess), and what it recently
 * concluded. Rows open the task drawer, where the full evidence report lives.
 */
export const CalibrationAgentPanel = ({ orgLookup }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const agenda = useSelector((state) => state.promptFeedback.optimizations.agenda)
  const loading = useSelector((state) => state.promptFeedback.optimizations.agendaLoading)

  const [agendaFailed, setAgendaFailed] = useState(false)
  const [page, setPage] = useState(1)
  const [recentPages, setRecentPages] = useState(null)

  const loadAgenda = async () => {
    const data = await dispatch(fetchFeedbackOptimizationAgenda(page))
    setAgendaFailed(isNil(data))
    if (data) {
      setRecentPages(data)
    }
  }

  useEffect(() => {
    loadAgenda()
  }, [dispatch, page])

  const hasActive = agenda.some(isActive)
  useEffect(() => {
    if (!hasActive) {
      return undefined
    }
    const timer = setInterval(loadAgenda, CALIBRATION_POLL_MS)
    return () => clearInterval(timer)
  }, [hasActive, page])

  const openTask = (item) => {
    history.push({
      search: queryString.stringify({ id: item.task_id, org_id: item.organization_id }),
    })
  }

  const active = agenda.filter(isActive)
  const upNext = agenda.filter((item) => item.upcoming && !item.optimization_id)
  const recent = agenda.filter((item) => item.optimization_id && !isActive(item))

  return (
    <div className="agent-panel" data-testid="calibration-agent-panel">
      {orgLookup && (
        <div className="agent-panel__header">
          <AgentSettings orgLookup={orgLookup} />
        </div>
      )}
      {agendaFailed && <ErrorMessage content="Could not load the agent's agenda." />}
      {loading && isEmpty(agenda) ? (
        <Loader centered />
      ) : (
        <>
          {isEmpty(agenda) && !agendaFailed && (
            <InboxZero
              currentTab={{ id: AGENT_TAB_ID }}
              emptyTitle="Nothing on the agenda"
              emptyDescription="Reports are picked up automatically every hour"
            />
          )}
          <Group title="Working on now" items={active} orgLookup={orgLookup} openTask={openTask} />
          <Group title="Up next" items={upNext} orgLookup={orgLookup} openTask={openTask} />
          <Group title="Recent runs" items={recent} orgLookup={orgLookup} openTask={openTask} />
          {recentPages?.recent_total_pages > 1 && (
            <AdvancedTablePagination
              activePage={page}
              rowsPerPage={recentPages.recent_limit}
              count={recentPages.recent_total}
              totalPages={recentPages.recent_total_pages}
              setActivePage={setPage}
              transparent
              label="run"
            />
          )}
        </>
      )}
    </div>
  )
}
